import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart, waLink, formatAMD } from '@/context/CartContext';

export default function Contact() {
  const { t } = useTranslation();
  const { lines, total } = useCart();

  const [bName, setBName] = useState('');
  const [bPhone, setBPhone] = useState('');
  const [bGuests, setBGuests] = useState('2');
  const [bDate, setBDate] = useState('');
  const [bTime, setBTime] = useState('');
  const [bNote, setBNote] = useState('');

  const [oName, setOName] = useState('');
  const [oPhone, setOPhone] = useState('');
  const [oAddress, setOAddress] = useState('');

  const sendBooking = () => {
    const msg = [
      t('booking.title'),
      '',
      `${t('booking.name')}: ${bName}`,
      `${t('booking.phone')}: ${bPhone}`,
      `${t('booking.guests')}: ${bGuests}`,
      `${t('booking.date')}: ${bDate}`,
      `${t('booking.time')}: ${bTime}`,
      `${t('booking.note')}: ${bNote || '—'}`,
    ].join('\n');
    window.open(waLink(msg), '_blank');
  };

  const sendOrder = () => {
    const items = lines.map((l) => `• ${l.qty} x ${l.item.name} — ${l.item.price}`).join('\n');
    const msg = [
      t('order.title'),
      '',
      items || t('order.empty'),
      '',
      `${t('order.total')}: ${formatAMD(total)}`,
      '',
      `${t('order.name')}: ${oName}`,
      `${t('order.phone')}: ${oPhone}`,
      `${t('order.address')}: ${oAddress}`,
    ].join('\n');
    window.open(waLink(msg), '_blank');
  };

  const inputCls =
    'w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30';

  return (
    <section id="contact" className="w-full bg-background-50 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center justify-center gap-2 text-xs font-semibold tracking-[0.3em] uppercase text-primary-600">
            <span className="h-px w-8 bg-primary-500"></span>
            {t('booking.eyebrow')} &amp; {t('order.eyebrow')}
            <span className="h-px w-8 bg-primary-500"></span>
          </span>
          <h2 className="mt-4 font-heading text-3xl font-semibold leading-tight text-foreground-950 md:text-4xl">
            {t('booking.title')} &amp; {t('order.title')}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">
            {t('booking.subtitle')} {t('order.subtitle')}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Book a Table */}
          <div className="rounded-lg bg-background-100 p-6 md:p-8">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50">
                <i className="ri-calendar-check-line text-xl"></i>
              </span>
              <div>
                <h3 className="font-heading text-xl font-semibold text-foreground-950">{t('booking.title')}</h3>
                <p className="text-sm text-foreground-600">{t('booking.subtitle')}</p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="bName" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  {t('booking.name')}
                </label>
                <input id="bName" value={bName} onChange={(e) => setBName(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label htmlFor="bPhone" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  {t('booking.phone')}
                </label>
                <input id="bPhone" type="tel" value={bPhone} onChange={(e) => setBPhone(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label htmlFor="bGuests" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  {t('booking.guests')}
                </label>
                <select id="bGuests" value={bGuests} onChange={(e) => setBGuests(e.target.value)} className={inputCls}>
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', '10+'].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="bDate" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  {t('booking.date')}
                </label>
                <input id="bDate" type="date" value={bDate} onChange={(e) => setBDate(e.target.value)} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="bTime" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  {t('booking.time')}
                </label>
                <input id="bTime" type="time" value={bTime} onChange={(e) => setBTime(e.target.value)} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="bNote" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  {t('booking.note')}
                </label>
                <textarea
                  id="bNote"
                  rows={2}
                  maxLength={500}
                  value={bNote}
                  onChange={(e) => setBNote(e.target.value)}
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                ></textarea>
              </div>
            </div>

            <button
              type="button"
              onClick={sendBooking}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              {t('booking.send')}
            </button>
          </div>

          {/* Create an Order */}
          <div className="rounded-lg bg-background-100 p-6 md:p-8">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-500 text-background-50">
                <i className="ri-shopping-bag-3-line text-xl"></i>
              </span>
              <div>
                <h3 className="font-heading text-xl font-semibold text-foreground-950">{t('order.title')}</h3>
                <p className="text-sm text-foreground-600">{t('order.subtitle')}</p>
              </div>
            </div>

            {lines.length === 0 ? (
              <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-6 text-center">
                <p className="text-sm text-foreground-600">{t('order.empty')}</p>
                <a
                  href="#menu"
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
                >
                  {t('order.browse')}
                </a>
              </div>
            ) : (
              <div className="mt-6 rounded-lg bg-background-50 p-4">
                <ul className="flex flex-col gap-2">
                  {lines.map((l) => (
                    <li key={l.item.id} className="flex items-center justify-between gap-3 text-sm">
                      <span className="flex items-center gap-2 text-foreground-800">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-500 text-xs font-semibold text-background-50">
                          {l.qty}
                        </span>
                        {l.item.name}
                      </span>
                      <span className="whitespace-nowrap text-foreground-600">{l.item.price}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3">
                  <span className="text-sm font-semibold text-foreground-950">{t('order.total')}</span>
                  <span className="font-heading text-lg font-semibold text-primary-600">{formatAMD(total)}</span>
                </div>
              </div>
            )}

            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="oName" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  {t('order.name')}
                </label>
                <input id="oName" value={oName} onChange={(e) => setOName(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label htmlFor="oPhone" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  {t('order.phone')}
                </label>
                <input id="oPhone" type="tel" value={oPhone} onChange={(e) => setOPhone(e.target.value)} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="oAddress" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  {t('order.address')}
                </label>
                <input id="oAddress" value={oAddress} onChange={(e) => setOAddress(e.target.value)} className={inputCls} />
              </div>
            </div>

            <button
              type="button"
              onClick={sendOrder}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent-500 px-6 py-3.5 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-accent-600"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              {t('order.send')}
            </button>
          </div>
        </div>

        {/* Info cards */}
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg bg-background-100 p-5">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-map-pin-2-line text-xl"></i>
            </span>
            <h4 className="mt-4 font-heading text-base font-semibold text-foreground-950">{t('info.visit')}</h4>
            <p className="mt-1 text-sm leading-relaxed text-foreground-700">{t('info.address')}</p>
          </div>
          <div className="rounded-lg bg-background-100 p-5">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-500 text-background-50">
              <i className="ri-phone-line text-xl"></i>
            </span>
            <h4 className="mt-4 font-heading text-base font-semibold text-foreground-950">{t('info.call')}</h4>
            <a href="tel:+37493951149" className="mt-1 block text-sm text-foreground-700 hover:text-primary-600">
              {t('info.phone1')}
            </a>
            <a
              href={waLink(`${t('booking.title')}!`)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 flex items-center gap-1.5 text-sm text-foreground-700 hover:text-primary-600"
            >
              <i className="ri-whatsapp-line"></i>WhatsApp
            </a>
          </div>
          <div className="rounded-lg bg-background-100 p-5">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-500 text-background-50">
              <i className="ri-time-line text-xl"></i>
            </span>
            <h4 className="mt-4 font-heading text-base font-semibold text-foreground-950">{t('info.open')}</h4>
            <p className="mt-1 text-sm leading-relaxed text-foreground-700">{t('info.hours')}</p>
          </div>
          <div className="rounded-lg bg-background-100 p-5">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-bike-line text-xl"></i>
            </span>
            <h4 className="mt-4 font-heading text-base font-semibold text-foreground-950">{t('info.delivery')}</h4>
            <p className="mt-1 text-sm leading-relaxed text-foreground-700">{t('info.deliv')}</p>
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-lg">
          <iframe
            title="Mehfil 24/7 location on Google Maps"
            src="https://www.google.com/maps?q=28%20Rubinyants%20St%2C%20Yerevan%200069%2C%20Armenia&output=embed"
            className="h-80 w-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          ></iframe>
        </div>
      </div>
    </section>
  );
}